<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div
    v-if="targetingCriteria != null && targetingCriteria.some((x) => x.isUpdatePushedExternally)"
    id="custom_event_targeting">
    <div class="form-section group collapsable">
      <span class="icon"><icon-colored-event /></span>
      {{ capitalize($t('names.customEvent', { n: 2 })) }}
      <div
        v-if="canBeRemoved"
        class="actions">
        <button
          class="btn btn-danger btn-sm"
          @click="$emit('cleared')">
          <icon-delete />
          {{ capitalize($t('actions.remove')) }}
        </button>
      </div>
    </div>

    <transition name="fade">
      <div
        v-if="isExpanded"
        class="form-section-content px-0 pb-0"
        :class="isExpanded ? 'form-section-expanded' : 'form-section-collapsed'">
        <div
          v-for="cat in customEventCategories"
          :key="cat.name"
          class="form-group row custom-event-categories">
          <form-label for-i-d="edit_lineitem_customevents">{{ cat.name }} </form-label>
          <div class="col-sm-8">
            <multiselect
              v-model="cat.selectedSubTypes"
              :options="cat.options"
              label="name"
              track-by="code"
              :placeholder="capitalize($t('messages.selectOne'))"
              :allow-empty="true"
              :disabled="isReadOnly"
              :show-labels="true"
              :selected-label="capitalize($t('states.selected'))"
              :select-label="capitalize($t('actions.select'))"
              :deselect-label="capitalize($t('actions.remove'))"
              :searchable="true"
              :multiple="true"
              :close-on-select="false"
              @select="updateSelectedCustomEvents"
              @remove="updateSelectedCustomEvents" />
            <span
              v-show="$validator.messageBag.errors.has('customEvents')"
              class="help is-danger"
              >{{ $validator.messageBag.errors.first('customEvents') }}</span
            >
          </div>
        </div>
        <div class="disclaimer">
          <span>{{ $t('messages.lineItemWillTriggerWhenAllTheConditionsAreMet') }}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, onBeforeMount, Ref, ref, toRefs, watch } from 'vue';

  import { FormLabel } from '@/common/components/controls';
  import { IconColoredEvent } from '@/common/icons/colored';
  import { IconDelete } from '@/common/icons/monochrome';
  import TargetingCriterionSubType from '@/common/models/targeting-criteria/TargetingCriterionSubType';
  import { areArraysEqual } from '@/globals';
  import { useTargetingCriteria } from '@/org-app/services/tanstack/queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';

  //
  // Props
  //
  interface IProps {
    selectedCustomEvents: TargetingCriterionSubType[];
    isReadOnly: boolean;
    canBeRemoved: boolean;
  }
  const props = withDefaults(defineProps<IProps>(), {
    selectedCustomEvents: () => [],
    isReadOnly: false,
    canBeRemoved: false,
  });
  const { selectedCustomEvents, isReadOnly, canBeRemoved } = toRefs(props);

  //
  // Emits
  //
  const emit = defineEmits(['changed', 'cleared']);

  //
  // Data
  //
  const customEventCategories = ref(null);
  const isExpanded = ref(true);
  const selectedCustomEventsInternal: Ref<TargetingCriterionSubType[]> = ref([]);

  //
  // State
  //
  const targetingCriteriaQuery = useTargetingCriteria();

  //
  // Computed
  //
  const targetingCriteria = computed(() => targetingCriteriaQuery.items.value);
  const customEvents = computed(() => {
    const externalPushedTargetCriteria = targetingCriteria.value.filter((x) => x.isUpdatePushedExternally);
    return externalPushedTargetCriteria.map((x) => x.subTypes).flat();
  });

  //
  // Watchers
  //
  watch(selectedCustomEvents, (val) => {
    selectedCustomEventsInternal.value = [...val];
  });

  watch(selectedCustomEventsInternal, (val, oldVal) => {
    if (!areArraysEqual(val, oldVal)) {
      emit('changed', val);
    }
  });

  //
  // Methods
  //
  function updateSelectedCustomEvents() {
    setTimeout(() => {
      selectedCustomEventsInternal.value = customEventCategories.value
        .filter((x) => x.selectedSubTypes != null)
        .map((x) => x.selectedSubTypes)
        .reduce((mem, x) => [...mem, ...x], []);
    }, 100);
  }

  function setupSelectedCustomEvents() {
    if (targetingCriteria.value == null || selectedCustomEvents.value == null) {
      return;
    }
    selectedCustomEvents.value.forEach((x) => {
      const cat = customEventCategories.value.find((y) => y.name === x.categoryName);
      if (cat != null) {
        if (cat.selectedSubTypes == null) {
          cat.selectedSubTypes = [];
        }
        cat.selectedSubTypes.push(x);
      }
    });
  }

  //
  // Lifecycle Hooks
  //
  onBeforeMount(async () => {
    await waitForPendingQueries([targetingCriteriaQuery]);
    const externalPushedTargetCriteria = targetingCriteria.value.filter((x) => x.isUpdatePushedExternally);
    const allSubTypes = externalPushedTargetCriteria.map((x) => x.subTypes).flat();
    customEventCategories.value = Array.from(new Set(allSubTypes.map((x) => x.categoryName))).map((x) => {
      return {
        name: x,
        selectedSubTypes: null,
        options: customEvents.value
          .filter((y) => y.categoryName === x)
          .map((y) => {
            return {
              isActive: y.isActive,
              code: y.code,
              name: y.name,
              uuid: y.uuid,
              categoryName: y.categoryName,
            };
          }),
      };
    });
    selectedCustomEventsInternal.value = [...selectedCustomEvents.value];
    setupSelectedCustomEvents();
  });
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  #custom_event_targeting {
    .disclaimer {
      padding: 1rem 2rem;
      margin-top: 2rem;
    }
    .custom-event-categories {
      padding: 0rem 1.6rem;
    }
  }
</style>
